import React from 'react';
import { useLocale } from '@platform/core';
import { ProjectFilters as ProjectFiltersType, ProjectStatus } from '../../types/budget.types';

interface ProjectFiltersProps {
  filters: ProjectFiltersType;
  onChange: (filters: Partial<ProjectFiltersType>) => void;
  projectManagers: string[];
  managers: string[];
  clients: string[];
  contracts: string[];
}

export const ProjectFilters: React.FC<ProjectFiltersProps> = ({
  filters,
  onChange,
  projectManagers,
  managers,
  clients,
  contracts,
}) => {
  const { t } = useLocale();

  return (
    <div className="project-filters">
      <div className="filters-row">
        <div className="filter-group search-group">
          <input
            type="text"
            className="filter-input"
            placeholder={t('budget.projects.typeToSearch')}
            value={filters.search}
            onChange={(e) => onChange({ search: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={filters.status}
            onChange={(e) => onChange({ status: e.target.value as 'all' | ProjectStatus })}
          >
            <option value="all">{t('budget.projects.statusAll')}</option>
            <option value="active">{t('budget.projects.statusActive')}</option>
            <option value="completed">{t('budget.projects.statusCompleted')}</option>
            <option value="future">{t('budget.projects.statusFuture')}</option>
          </select>
        </div>

        {projectManagers.length > 0 && (
          <div className="filter-group">
            <select
              className="filter-select"
              value={filters.projectManager}
              onChange={(e) => onChange({ projectManager: e.target.value })}
            >
              <option value="">{t('budget.projects.projectManager')}: {t('common.all')}</option>
              {projectManagers.map(pm => (
                <option key={pm} value={pm}>{pm}</option>
              ))}
            </select>
          </div>
        )}

        {managers.length > 0 && (
          <div className="filter-group">
            <select
              className="filter-select"
              value={filters.manager}
              onChange={(e) => onChange({ manager: e.target.value })}
            >
              <option value="">{t('budget.projects.manager')}: {t('common.all')}</option>
              {managers.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        )}

        {clients.length > 0 && (
          <div className="filter-group">
            <select
              className="filter-select"
              value={filters.client}
              onChange={(e) => onChange({ client: e.target.value })}
            >
              <option value="">{t('budget.projects.client')}: {t('common.all')}</option>
              {clients.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}

        {contracts.length > 0 && (
          <div className="filter-group">
            <select
              className="filter-select"
              value={filters.contract}
              onChange={(e) => onChange({ contract: e.target.value })}
            >
              <option value="">{t('budget.projects.contract')}: {t('common.all')}</option>
              {contracts.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="filters-row">
        <div className="filter-group date-group">
          <label>{t('budget.projects.fromDate')}:</label>
          <input
            type="date"
            className="filter-input"
            value={filters.dateFrom}
            onChange={(e) => onChange({ dateFrom: e.target.value })}
          />
        </div>

        <div className="filter-group date-group">
          <label>{t('budget.projects.toDate')}:</label>
          <input
            type="date"
            className="filter-input"
            value={filters.dateTo}
            onChange={(e) => onChange({ dateTo: e.target.value })}
          />
        </div>

        <div className="filter-spacer" />

        {(filters.search || filters.status !== 'all' || filters.projectManager || filters.manager || filters.client || filters.contract || filters.dateFrom || filters.dateTo) && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onChange({
              search: '',
              status: 'all',
              projectManager: '',
              manager: '',
              client: '',
              contract: '',
              dateFrom: '',
              dateTo: '',
            })}
          >
            {t('common.clearFilters')}
          </button>
        )}
      </div>

      <style>{`
        .project-filters {
          background: var(--color-surface);
          border-radius: var(--radius);
          box-shadow: var(--shadow);
          padding: 1rem;
          margin-bottom: 1rem;
        }

        .filters-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: center;
        }

        .filters-row + .filters-row {
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid var(--color-border);
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .filter-group label {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          white-space: nowrap;
        }

        .search-group {
          flex: 1;
          min-width: 200px;
        }

        .date-group {
          min-width: 180px;
        }

        .filter-input {
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          font-size: 0.875rem;
          background: var(--color-background);
        }

        .search-group .filter-input {
          width: 100%;
        }

        .filter-select {
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          font-size: 0.875rem;
          min-width: 150px;
          background: var(--color-background);
        }

        .filter-spacer {
          flex: 1;
        }

        .btn-sm {
          padding: 0.375rem 0.75rem;
          font-size: 0.75rem;
        }

        .btn-secondary {
          background: var(--color-background);
          color: var(--color-text);
          border: 1px solid var(--color-border);
        }

        .btn-secondary:hover {
          background: var(--color-border);
        }
      `}</style>
    </div>
  );
};
